import { useTranslation } from "@/core/i18n";
import { Icon } from "atoms/icon/Icon";
import { formatCurrency } from "toolbox/Money";

import Beautifi from "../../icon/icons/beautifi.svg";
import InfoIcon from "../../icon/icons/info.svg";

// Beautifi order bounds, in cents of the platform's own currency (integration-design.md §8.1;
// Beautifi publishes its limits in CAD). NOTE: these are cents, unlike
// AFFIRM_MINIMUM_AMOUNT which is dollars — compare them against cartDetails.estimatedTotal directly.
export const BEAUTIFI_MINIMUM_AMOUNT = 50_000; // CAD $500.00
export const BEAUTIFI_MAXIMUM_AMOUNT = 5_000_000; // CAD $50,000.00

export enum BeautifiUnavailableReason {
  ContainsMembership = "ContainsMembership",
  BelowMinimum = "BelowMinimum",
  AboveMaximum = "AboveMaximum",
}

interface NotAvailableBeautifiProps {
  reason: BeautifiUnavailableReason;
  isLargeBanner: boolean;
}

export const NotAvailableBeautifi = ({ reason, isLargeBanner }: NotAvailableBeautifiProps) => {
  const { t } = useTranslation();
  const classes = {
    textMessage: isLargeBanner ? "mb-2 text-body1" : "pl-3 pr-4 text-body2",
    icon: isLargeBanner ? "pr-2 pt-2" : "pl-3",
    wrapper: `rounded bg-background-two ${isLargeBanner ? "px-3 py-6" : "items-center py-2.5"}`,
  };

  const formatAmount = (cents: number) => formatCurrency(cents, 2);

  const getBeautifiMessage = () => {
    switch (reason) {
      case BeautifiUnavailableReason.ContainsMembership:
        return t("notAvailableBeautifi.notAvailableWithMembership");
      case BeautifiUnavailableReason.AboveMaximum:
        return t("notAvailableBeautifi.availableUpTo", {
          amount: formatAmount(BEAUTIFI_MAXIMUM_AMOUNT),
        });
      case BeautifiUnavailableReason.BelowMinimum:
      default:
        return t("notAvailableBeautifi.availableFromMinimum", {
          amount: formatAmount(BEAUTIFI_MINIMUM_AMOUNT),
        });
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={`flex ${isLargeBanner ? "" : "items-center"}`}>
        <div className={classes.icon}>
          <Icon svg={InfoIcon} size={isLargeBanner ? "small" : "xsmall"} color="secondaryText" />
        </div>
        <div className="flex flex-col">
          <div className={classes.textMessage}>
            <Beautifi
              role="img"
              aria-label={t("common.beautifiLogoLabel")}
              className={`inline-block ${isLargeBanner ? "h-5" : "h-4"} align-middle`}
            />{" "}
            {getBeautifiMessage()}
          </div>
          {isLargeBanner && (
            <div className="text-body2 text-secondary">
              {t("notAvailableBeautifi.availableBetweenRange", {
                min: formatAmount(BEAUTIFI_MINIMUM_AMOUNT),
                max: formatAmount(BEAUTIFI_MAXIMUM_AMOUNT),
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
